import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, QrCode, Building2, Sparkles, PhoneCall, Hotel, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import appLogoDefault from "@/assets/images/logo.png";

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>The Hotelier - Hospitality & Property Management</title>
        <meta
          name="description"
          content="The Hotelier Management and In-Room Guest Services Portal"
        />
      </Helmet>

      <div className="min-h-dvh h-dvh bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900 flex flex-col justify-between p-4 sm:p-6 lg:p-8 overflow-hidden select-none">
        {/* Soft background ambient lighting */}
        <div className="pointer-events-none absolute -top-28 left-1/2 -translate-x-1/2 h-[350px] w-[600px] rounded-full bg-[#923536]/5 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 right-0 h-[250px] w-[250px] rounded-full bg-emerald-500/5 blur-3xl" />
        <div className="pointer-events-none absolute top-1/2 -left-20 h-[300px] w-[300px] rounded-full bg-indigo-500/5 blur-3xl" />

        <div className="max-w-3xl w-full mx-auto my-auto flex flex-col justify-center space-y-6 sm:space-y-7 z-10">
          {/* Header Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center space-y-3"
          >
            {/* Prominent & Distinct Official Logo */}
            <div className="relative mx-auto inline-flex items-center justify-center mb-1">
              <div className="absolute -inset-2 rounded-3xl bg-[#923536]/15 blur-lg opacity-80" />
              <div className="relative h-24 w-24 sm:h-28 sm:w-28 rounded-3xl bg-white p-3 shadow-2xl shadow-slate-900/10 border border-slate-200/90 ring-8 ring-[#923536]/10 transition-all duration-300 hover:scale-105 hover:ring-[#923536]/20">
                <img
                  src={appLogoDefault}
                  alt="The Hotelier Logo"
                  className="h-full w-full object-contain drop-shadow-sm"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-amber-50 border border-amber-200/80 text-[11px] font-bold text-amber-800 shadow-2xs">
                <Sparkles className="h-3 w-3 text-amber-600" />
                <span>Hospitality Intelligence Suite</span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900">
                The Hotelier
              </h1>
              <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto leading-relaxed font-medium">
                Select your portal to access live operations, in-room dining, online guest calls, and concierge services.
              </p>
            </div>
          </motion.div>

          {/* Cards Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 items-stretch">
            {/* Guest Portal Card */}
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="flex flex-col h-full"
            >
              <Card className="flex flex-col h-full rounded-3xl border border-slate-200/90 bg-white/95 backdrop-blur-xl shadow-xl shadow-slate-900/5 hover:border-slate-300 hover:shadow-2xl transition-all duration-200">
                <CardHeader className="p-5 sm:p-6 pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="h-10 w-10 rounded-2xl bg-emerald-50 border border-emerald-200/80 flex items-center justify-center text-emerald-600 shadow-2xs">
                      <QrCode className="w-5 h-5" />
                    </div>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 border border-emerald-200 text-emerald-700">
                      In-Room Guests
                    </span>
                  </div>
                  <CardTitle className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
                    Guest Access
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-600 mt-1 leading-relaxed line-clamp-2">
                    Access room dining, order amenities, request housekeeping, and explore local tourism.
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col justify-end p-5 sm:p-6 pt-2 mt-auto">
                  <div className="rounded-2xl border border-slate-200/80 bg-slate-50/80 p-3.5 text-xs text-slate-600 leading-relaxed font-medium">
                    Scan the permanent QR code in your room to open your guest portal.
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Hotel Management Card */}
            <motion.div
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              className="flex flex-col h-full"
            >
              <Card className="flex flex-col h-full rounded-3xl border border-slate-200/90 bg-white/95 backdrop-blur-xl shadow-xl shadow-slate-900/5 hover:border-[#923536]/40 hover:shadow-2xl transition-all duration-200">
                <CardHeader className="p-5 sm:p-6 pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="h-10 w-10 rounded-2xl bg-[#923536]/10 border border-[#923536]/20 flex items-center justify-center text-[#923536] shadow-2xs">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 border border-rose-200 text-rose-700">
                      Staff & Admin
                    </span>
                  </div>
                  <CardTitle className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
                    Hotel Management
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-600 mt-1 leading-relaxed line-clamp-2">
                    Staff portal to manage guest requests, direct online calls, POS orders, and room statuses.
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col justify-end space-y-3 p-5 sm:p-6 pt-2 mt-auto">
                  <div className="rounded-2xl border border-slate-200/80 bg-slate-50/80 p-3.5 text-xs text-slate-600 leading-relaxed font-medium">
                    Authorized personnel only. Access the dashboard to monitor operations.
                  </div>

                  <Button
                    onClick={() => navigate("/hotel/dashboard")}
                    className="w-full h-11 rounded-2xl font-bold text-xs sm:text-sm bg-gradient-to-r from-[#923536] to-[#b34042] text-white hover:from-[#a03b3d] hover:to-[#c6494b] shadow-lg shadow-[#923536]/25 border border-white/20 group transition-all duration-200 active:scale-[0.99]"
                  >
                    <span>Go to Dashboard</span>
                    <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Footer Badges */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3.5 text-[11px] text-slate-600 font-semibold pt-1">
            <span className="flex items-center gap-1.5 bg-white border border-slate-200/90 shadow-2xs px-3 py-1.5 rounded-full">
              <PhoneCall className="h-3.5 w-3.5 text-emerald-600" />
              Online Calls & Alerts
            </span>
            <span className="flex items-center gap-1.5 bg-white border border-slate-200/90 shadow-2xs px-3 py-1.5 rounded-full">
              <Hotel className="h-3.5 w-3.5 text-amber-600" />
              Operations Dashboard
            </span>
            <span className="flex items-center gap-1.5 bg-white border border-slate-200/90 shadow-2xs px-3 py-1.5 rounded-full">
              <ShieldCheck className="h-3.5 w-3.5 text-sky-600" />
              Role Permissions
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;
